import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useDemo } from "@/context/DemoContext";
import { useHousehold } from "@/context/HouseholdContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import DuplicateDialog from "@/components/DuplicateDialog";
import { UploadSimple, Tray, ArrowsClockwise, FilePdf, ArrowRight, EnvelopeSimple, Trash, ArrowCounterClockwise, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending_review: "bg-foreground text-background",
  duplicate_review: "bg-destructive text-destructive-foreground",
  parsed: "bg-success/10 text-success",
  processed: "bg-secondary text-muted-foreground",
};
const STATUS_LABEL = { pending_review: "Needs Review", duplicate_review: "Possible Duplicate", parsed: "Parsed", processed: "Processed" };

const getBadge = (b) => {
  if (b.status === "pending_review" && b.revision_of) {
    return { label: "Revised Bill", className: "bg-warning/10 text-warning" };
  }
  if (b.status === "pending_review" && b.duplicate_resolved === "not_duplicate") {
    return { label: "Not a Duplicate", className: "bg-success/10 text-success" };
  }
  return { label: STATUS_LABEL[b.status] || b.status, className: STATUS_STYLES[b.status] || "bg-secondary text-muted-foreground" };
};

export default function Inbox() {
  const { fmtMoney } = useDemo();
  const { hid } = useHousehold();
  const [bills, setBills] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadPhase, setUploadPhase] = useState("uploading"); // uploading | analyzing
  const [dupBill, setDupBill] = useState(null);
  const [gmail, setGmail] = useState({ connected: false, configured: false });
  const fileRef = useRef();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const load = () => api.get("/bills").then((r) => setBills(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/gmail/status").then((r) => setGmail(r.data)).catch(() => {});
    if (params.get("gmail") === "connected") toast.success("Gmail connected");
  }, [params]);

  const openDuplicate = async (billId) => {
    try {
      const full = await api.get(`/bills/${billId}`);
      setDupBill(full.data);
    } catch (e) { navigate(`/bills-inbox/${billId}`); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadPct(0); setUploadPhase("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/bills/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setUploadPct(pct);
            if (pct >= 100) setUploadPhase("analyzing");
          }
        },
      });
      setUploading(false);
      if (res.data.duplicate) {
        await openDuplicate(res.data.id);
      } else {
        toast.success("Statement uploaded");
        navigate(`/bills-inbox/${res.data.id}`);
      }
    } catch (e) {
      setUploading(false);
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === "duplicate_document") {
        toast.error(detail.message || "This bill already exists.", { duration: 7000 });
        if (detail.duplicate_of?.id) navigate(`/bills-inbox/${detail.duplicate_of.id}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Upload failed");
      }
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const rowClick = (b) => {
    if (b.status === "duplicate_review") openDuplicate(b.id);
    else navigate(`/bills-inbox/${b.id}`);
  };

  const scan = async () => {
    setScanning(true);
    try {
      const res = await api.post("/gmail/scan");
      toast.success(`Scanned inbox — ${res.data.created} new statement(s) found`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this bill? Any ledger entries it created will also be removed.")) return;
    await api.delete(`/bills/${id}`);
    toast.success("Bill deleted and ledger entries removed");
    load();
  };

  const restage = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Restage this bill? Its ledger entries will be removed and it returns to the inbox for re-processing.")) return;
    const res = await api.post(`/bills/${id}/reset`);
    toast.success(`Restaged — ${res.data.transactions_removed} ledger entr${res.data.transactions_removed === 1 ? "y" : "ies"} removed`);
    load();
  };

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Ingestion</p>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Bill Inbox</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <span
            data-testid="gmail-status-chip"
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${
              gmail.connected
                ? "bg-success/10 text-success"
                : gmail.configured
                  ? "bg-warning/10 text-warning"
                  : "bg-secondary text-muted-foreground"
            }`}
          >
            {gmail.connected ? "Gmail connected" : gmail.configured ? "Gmail ready" : "Gmail not connected"}
          </span>
          <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={onUpload} data-testid="bill-upload-input" />
          <Button data-testid="upload-bill-button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-sm font-semibold">
            <UploadSimple size={16} className="mr-1" /> {uploading ? "Uploading…" : "Upload PDF"}
          </Button>
          {gmail.connected ? (
            <Button data-testid="scan-gmail-button" onClick={scan} disabled={scanning} className="rounded-sm font-semibold transition-transform hover:-translate-y-[1px]">
              <ArrowsClockwise size={16} className={`mr-1 ${scanning ? "animate-spin" : ""}`} /> {scanning ? "Scanning…" : "Scan Gmail"}
            </Button>
          ) : (
            <Button data-testid="connect-gmail-cta" onClick={() => navigate("/settings")} className="rounded-sm font-semibold">
              <EnvelopeSimple size={16} className="mr-1" /> Connect Gmail
            </Button>
          )}
        </div>
      </div>

      {bills.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <Tray size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="font-display text-xl font-bold">Inbox is empty</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Upload a statement PDF or scan your connected Gmail. We'll parse it with AI and attribute each
            expense to the right property.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {bills.map((b) => (
            <div
              key={b.id}
              data-testid={`bill-row-${b.id}`}
              onClick={() => rowClick(b)}
              className="bg-card border border-border rounded-sm p-5 flex items-center gap-4 cursor-pointer hover:shadow-md hover:-translate-y-[1px] transition-all group"
            >
              <div className="h-11 w-11 bg-secondary flex items-center justify-center rounded-sm shrink-0">
                <FilePdf size={22} weight="fill" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{b.vendor || b.subject || b.filename}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {b.source === "gmail" ? `From ${b.sender}` : "Manual upload"}
                  {b.statement_date ? ` · ${b.statement_date}` : ""}
                </p>
              </div>
              {b.total_amount > 0 && (
                <div className="text-right hidden sm:block">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
                  <p className="font-mono font-bold tabular-nums">{fmtMoney(b.total_amount)}</p>
                </div>
              )}
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm shrink-0 ${getBadge(b).className}`}>
                {getBadge(b).label}
              </span>
              {(b.status === "processed" || b.status === "parsed") && (
                <button data-testid={`restage-bill-${b.id}`} title="Restage for re-processing" onClick={(e) => restage(b.id, e)} className="text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowCounterClockwise size={16} />
                </button>
              )}
              <button data-testid={`delete-bill-${b.id}`} title="Delete bill" onClick={(e) => remove(b.id, e)} className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash size={16} />
              </button>
              <ArrowRight size={18} className="text-muted-foreground group-hover:translate-x-1 transition-transform shrink-0" />
            </div>
          ))}
        </div>
      )}


      {/* Upload progress popup */}
      <Dialog open={uploading} onOpenChange={() => {}}>
        <DialogContent data-testid="upload-progress-dialog" className="rounded-sm max-w-sm">
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2">
              {uploadPhase === "analyzing"
                ? <Sparkle size={20} weight="fill" className="text-foreground animate-pulse" />
                : <UploadSimple size={20} weight="bold" />}
              <p className="font-display text-lg font-bold tracking-tight">
                {uploadPhase === "analyzing" ? "Analyzing document…" : "Uploading…"}
              </p>
            </div>
            <Progress data-testid="upload-progress-bar" value={uploadPhase === "analyzing" ? 100 : uploadPct} className="rounded-sm" />
            <p className="text-xs text-muted-foreground">
              {uploadPhase === "analyzing"
                ? "Reading the statement and checking for duplicates (vendor, account #, coverage period, amount)…"
                : `${uploadPct}% uploaded`}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Possible-duplicate popup (child window) */}
      <DuplicateDialog
        bill={dupBill}
        open={!!dupBill}
        onOpenChange={(o) => { if (!o) setDupBill(null); }}
        onResolved={() => { setDupBill(null); load(); }}
      />
    </div>
  );
}
